/**
 * Earthworks Employee Hours — clock-in hours (the PAY record) beside daily-log
 * hours (the BILLING record), per worker per day, with the API's mismatch
 * flag. The two are compared, never merged.
 *
 *   <EwJobEmployeeHoursSection>   the job page's "Employee hours" tab
 *                                 (GET /earthworks/jobs/:id/employee-hours)
 *   <EwEmployeeHoursScreen>       /earthworks/employees/:userId/hours — one
 *                                 worker across every job
 *                                 (GET /earthworks/employees/:userId/hours)
 *
 * Every figure and every flag is the API's (services/ew-employee-hours): this
 * file computes nothing. A side with nothing for the day arrives as null and
 * renders "—", never 0. The range defaults to the API's (the tenant's last two
 * weeks); the two date inputs narrow or widen it.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, Clock, NotebookPen } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { useEwEmployeeHours, useEwJobEmployeeHours } from '../../hooks/useEarthworks';
import { ewDay, fmtNumber } from '../../lib/earthworks';
import type { EwEmployeeHours, EwEmployeeHoursRow, EwHoursFlag } from '../../types/earthworks';
import { useBackToList, useOpenRecord } from '../downhole/recordPage';
import { EW_DATE_INPUT, EW_EYEBROW, EwBadge, EwEmpty, EwPanel, EwRegister, type EwColumn } from './EwBits';

type Row = EwEmployeeHoursRow & { id: string };

const hrs = (n: number | null | undefined) => (n === null || n === undefined ? '—' : `${fmtNumber(n)} h`);

const FLAG_SHORT: Record<EwHoursFlag, string> = {
  no_log: 'No log',
  no_clock: 'No clock-in',
  clock_open: 'Shift not counted',
  log_over_clock: 'Log over clock',
  clock_over_log: 'Clock over log',
};

function FlagCell({ row }: { row: EwEmployeeHoursRow }) {
  if (!row.flag) return <span className="text-fieldiq-muted">—</span>;
  return (
    <span className="inline-flex flex-col gap-1" data-testid="ew-hours-flag" data-flag={row.flag}>
      <EwBadge skin={{ label: FLAG_SHORT[row.flag] ?? row.flag, fill: 'bg-fi-amber', rail: 'border-l-fi-amber' }} value={row.flag} />
      {row.flagMessage && <span className="text-xs text-fieldiq-muted">{row.flagMessage}</span>}
    </span>
  );
}

type Range = { from: string; to: string };
const DAY_MS = 86_400_000;
const dayNumber = (d: string) => {
  const [y, m, dd] = d.split('-').map(Number);
  return Date.UTC(y, m - 1, dd) / DAY_MS;
};

/**
 * The two date inputs. They show what the reader picked, or the API's default
 * range until they pick. A cleared input stays cleared and that side falls
 * back to the API's default. A range the API would refuse (from after to, or
 * more than 366 days) is said here and NOT sent, so it never surfaces as a
 * load failure.
 */
function RangePicker({ apiFrom, apiTo, onChange }: { apiFrom?: string; apiTo?: string; onChange: (r: Range) => void }) {
  const [draft, setDraft] = useState<Range | null>(null);
  const from = draft ? draft.from : apiFrom ?? '';
  const to = draft ? draft.to : apiTo ?? '';
  const problem = (r: Range): string | null => {
    if (!r.from || !r.to) return null;
    if (r.from > r.to) return 'From must be on or before To.';
    if (dayNumber(r.to) - dayNumber(r.from) > 365) return 'Pick at most 366 days.';
    return null;
  };
  const set = (next: Range) => {
    setDraft(next);
    if (!problem(next)) onChange(next);
  };
  const error = draft ? problem(draft) : null;
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <input type="date" className={EW_DATE_INPUT} aria-label="From" value={from} max={to || undefined} onChange={(e) => set({ from: e.target.value, to })} />
        <span className="text-xs text-fieldiq-muted">to</span>
        <input type="date" className={EW_DATE_INPUT} aria-label="To" value={to} min={from || undefined} onChange={(e) => set({ from, to: e.target.value })} />
      </div>
      {error && (
        <p className="text-xs font-bold text-fi-amber" role="alert" data-testid="ew-hours-range-error">
          {error}
        </p>
      )}
    </div>
  );
}

function Summary({ data, apart = false }: { data: EwEmployeeHours | null; apart?: boolean }) {
  if (!data) return null;
  return (
    <p className="border-b border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted" data-testid="ew-hours-summary">
      {ewDay(data.from)} – {ewDay(data.to)} · clock-in {hrs(data.totals.clockHours)} · log {hrs(data.totals.logHours)} · standby{' '}
      {hrs(data.totals.standbyHours)} · {data.totals.flagged} {data.totals.flagged === 1 ? 'day' : 'days'} flagged. A day is flagged when the log
      is over the clock, the clock is more than {hrs(data.threshold)} over log + standby, a shift is still open or unconfirmed, or one side is missing.{apart ? " Shop time and other verticals' work are shown apart and not compared." : ''}
    </p>
  );
}

const withIds = (rows: EwEmployeeHoursRow[] | undefined): Row[] =>
  (rows ?? []).map((r) => ({ ...r, id: `${r.userId}:${r.workDate}` }));

const rail = (r: Row) => (r.flag ? 'border-l-fi-amber' : 'border-l-fi-steel');

export function EwJobEmployeeHoursSection({ jobId }: { jobId: string }) {
  const [range, setRange] = useState({ from: '', to: '' });
  const q = useEwJobEmployeeHours(jobId, range);
  const data = q.data?.data ?? null;
  const openRecord = useOpenRecord();
  const rows = withIds(data?.rows);
  const columns: EwColumn<Row>[] = [
    { key: 'date', header: 'Date', render: (r) => <span className="font-extrabold text-white">{ewDay(r.workDate)}</span> },
    { key: 'who', header: 'Worker', render: (r) => r.userName ?? '—' },
    { key: 'clock', header: 'Clock-in', align: 'right', render: (r) => hrs(r.clockHours) },
    { key: 'log', header: 'Log', align: 'right', render: (r) => hrs(r.logHours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (r) => hrs(r.standbyHours) },
    { key: 'total', header: 'Log + standby', align: 'right', render: (r) => hrs(r.logTotal) },
    { key: 'flag', header: 'Check', render: (r) => <FlagCell row={r} /> },
  ];
  return (
    <EwPanel
      eyebrow="Pay vs billing"
      title="Employee hours"
      testId="ew-job-employee-hours"
      actions={<RangePicker apiFrom={data?.from} apiTo={data?.to} onChange={setRange} />}
    >
      <Summary data={data} />
      <EwRegister
        query={q}
        rows={rows}
        columns={columns}
        rail={rail}
        onOpen={(r) => openRecord(`/earthworks/employees/${r.userId}/hours`)}
        subject="employee hours"
        testId="ew-hours"
        minWidth="min-w-[900px]"
        empty={<EwEmpty>No clock-ins or daily-log hours on this job in this range.</EwEmpty>}
        card={(r) => (
          <div>
            <p className="text-sm font-extrabold text-white">
              {ewDay(r.workDate)} · {r.userName ?? '—'}
            </p>
            <p className="mt-0.5 text-xs text-fieldiq-muted">
              Clock-in {hrs(r.clockHours)} · log {hrs(r.logHours)} · standby {hrs(r.standbyHours)}
            </p>
            {r.flag && (
              <div className="mt-2">
                <FlagCell row={r} />
              </div>
            )}
          </div>
        )}
      />
    </EwPanel>
  );
}

export default function EwEmployeeHoursScreen() {
  const { userId } = useParams<{ userId: string }>();
  const back = useBackToList('/earthworks/jobs');
  const [range, setRange] = useState({ from: '', to: '' });
  const q = useEwEmployeeHours(userId, range);
  const data = q.data?.data ?? null;
  const rows = withIds(data?.rows);
  const columns: EwColumn<Row>[] = [
    { key: 'date', header: 'Date', render: (r) => <span className="font-extrabold text-white">{ewDay(r.workDate)}</span> },
    { key: 'jobs', header: 'Jobs', render: (r) => (r.jobNumbers?.length ? r.jobNumbers.join(', ') : '—') },
    { key: 'clock', header: 'Clock-in', align: 'right', render: (r) => hrs(r.clockHours) },
    { key: 'log', header: 'Log', align: 'right', render: (r) => hrs(r.logHours) },
    { key: 'standby', header: 'Standby', align: 'right', render: (r) => hrs(r.standbyHours) },
    { key: 'total', header: 'Log + standby', align: 'right', render: (r) => hrs(r.logTotal) },
    { key: 'shop', header: 'Shop clock', align: 'right', render: (r) => hrs(r.shopClockHours) },
    { key: 'other', header: 'Other work', align: 'right', render: (r) => hrs(r.otherClockHours) },
    { key: 'flag', header: 'Check', render: (r) => <FlagCell row={r} /> },
  ];
  return (
    <div data-testid="ew-employee-hours-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={`${EW_EYEBROW} · Employee hours`}
        title={data?.employee?.name ?? (q.isLoading ? 'Employee' : '—')}
        description="Clock-in hours (pay) beside daily-log hours (billing), by day."
        onBack={back}
        backLabel="Back"
        stats={[
          { icon: <Clock className="size-4" aria-hidden="true" />, label: 'Clock-in', tone: 'neutral', loading: q.isLoading, value: hrs(data?.totals.clockHours) },
          { icon: <NotebookPen className="size-4" aria-hidden="true" />, label: 'Log', tone: 'neutral', loading: q.isLoading, value: hrs(data?.totals.logHours) },
          { icon: <AlertTriangle className="size-4" aria-hidden="true" />, label: 'Flagged days', tone: 'neutral', loading: q.isLoading, value: data ? String(data.totals.flagged) : '—' },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <EwPanel
            eyebrow="Pay vs billing"
            title="By day"
            testId="ew-employee-hours"
            actions={<RangePicker apiFrom={data?.from} apiTo={data?.to} onChange={setRange} />}
          >
            <Summary data={data} apart />
            <EwRegister
              query={q}
              rows={rows}
              columns={columns}
              rail={rail}
              subject="employee hours"
              testId="ew-employee-hours"
              minWidth="min-w-[1000px]"
              empty={<EwEmpty>No earthworks clock-ins or daily-log hours for this worker in this range.</EwEmpty>}
              card={(r) => (
                <div>
                  <p className="text-sm font-extrabold text-white">
                    {ewDay(r.workDate)} · {r.jobNumbers?.length ? r.jobNumbers.join(', ') : '—'}
                  </p>
                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                    Clock-in {hrs(r.clockHours)} · log {hrs(r.logHours)} · standby {hrs(r.standbyHours)}
                  </p>
                  {r.flag && (
                    <div className="mt-2">
                      <FlagCell row={r} />
                    </div>
                  )}
                </div>
              )}
            />
          </EwPanel>
        </div>
      </section>
    </div>
  );
}
